import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2, X, CheckCircle2, BookText, ChevronDown, ChevronUp } from "lucide-react";
import api from "@/lib/api";
import { JudgmentCard } from "@/components/JudgmentCard";
import { LegalQuestionSearch } from "@/components/LegalQuestionSearch";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/context/LanguageContext";
import { UMBRELLA_GROUPS, getGroup } from "@/lib/categories";
import { cn } from "@/lib/utils";
import Seo from "@/components/Seo";
import { PAGE_SEO, breadcrumbJsonLd, itemListJsonLd } from "@/lib/seo";

const PAGE_SIZE = 15;

export default function Updates() {
  const { t, lang } = useLanguage();
  const [params, setParams] = useSearchParams();

  // Active umbrella group (by name)
  const [activeGroup, setActiveGroupState] = useState(params.get("group") || "");
  // Active specific area (sub-filter)
  const [area, setAreaState] = useState(params.get("area") || "");
  // Active legal-provision filter, e.g. "Section 302, Indian Penal Code 1860"
  const [provision, setProvisionState] = useState(params.get("provision") || "");
  const [q, setQ] = useState(params.get("q") || "");
  const [skip, setSkip] = useState(0);
  const [allItems, setAllItems] = useState([]);
  const [total, setTotal] = useState(0);
  // Provision filter is hidden by default — toggle to reveal
  const [showProvisions, setShowProvisions] = useState(!!params.get("provision"));

  const resetPagination = () => {
    setSkip(0);
    setAllItems([]);
  };

  const setActiveGroup = (gName) => {
    setActiveGroupState(gName);
    setAreaState("");   // Clear sub-area when group changes
    resetPagination();
    const p = new URLSearchParams();
    if (gName) p.set("group", gName);
    if (provision) p.set("provision", provision);
    setParams(p);
  };

  const setArea = (val) => {
    setAreaState(val);
    resetPagination();
    const p = new URLSearchParams();
    if (activeGroup) p.set("group", activeGroup);
    if (val) p.set("area", val);
    if (provision) p.set("provision", provision);
    setParams(p);
  };

  const setProvision = (val) => {
    setProvisionState(val);
    resetPagination();
    const p = new URLSearchParams();
    if (activeGroup) p.set("group", activeGroup);
    if (area) p.set("area", area);
    if (val) p.set("provision", val);
    setParams(p);
  };

  // Keep provision in sync when arriving via a crawlable link (e.g. from a judgment page)
  useEffect(() => {
    const fromUrl = params.get("provision") || "";
    if (fromUrl !== provision) {
      setProvisionState(fromUrl);
      resetPagination();
    }
  }, [params]);

  const clearFilters = () => {
    setActiveGroupState("");
    setAreaState("");
    setProvisionState("");
    setQ("");
    setShowProvisions(false);
    resetPagination();
    setParams({});
  };

  const handleSearch = (val) => {
    setQ(val);
    resetPagination();
  };

  // All areas from DB for sub-filter
  const { data: allAreas } = useQuery({
    queryKey: ["areas"],
    queryFn: async () => (await api.get("/public/areas")).data,
  });

  // Sub-areas within selected group
  const subAreas = activeGroup
    ? (allAreas || []).filter((a) => {
        const g = getGroup(a);
        return g && g.name === activeGroup;
      })
    : [];

  // Active group object
  const activeGroupObj = UMBRELLA_GROUPS.find((g) => g.name === activeGroup) || null;

  // Distinct provisions cited across published judgments (for the Act/Section filter)
  const { data: provisionIndex = [] } = useQuery({
    queryKey: ["provisions-index"],
    queryFn: async () => (await api.get("/public/provisions?limit=40")).data,
    staleTime: 5 * 60 * 1000,
  });

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["judgments", activeGroup, area, q, provision, skip],
    queryFn: async () => {
      const p = new URLSearchParams();
      // Specific area overrides group
      if (area) {
        p.set("area", area);
      } else if (activeGroup) {
        p.set("group", activeGroup);
      }
      if (q) p.set("q", q);
      if (provision) p.set("provision", provision);
      p.set("limit", String(PAGE_SIZE));
      p.set("skip", String(skip));
      return (await api.get(`/public/judgments?${p.toString()}`)).data;
    },
    keepPreviousData: true,
    staleTime: 30000,
  });

  // useEffect(() => {
  //   if (!data) return;
  //   setTotal(data.total || 0);
  //   if (skip === 0) {
  //     setAllItems(data.items || []);
  //   } else {
  //     setAllItems((prev) => {
  //       const existingIds = new Set(prev.map((j) => j.id));
  //       return [...prev, ...(data.items || []).filter((j) => !existingIds.has(j.id))];
  //     });
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [data]);

  useEffect(() => {
    if (!data) return;
    setTotal(data.total || 0);
    if (skip === 0) {
      setAllItems(data.items || []);
    } else {
      setAllItems((prev) => {
        const existingIds = new Set(prev.map((j) => j.id));
        return [...prev, ...(data.items || []).filter((j) => !existingIds.has(j.id))];
      });
    }
   
  }, [data]);
  const hasMore = allItems.length < total;
  const hasActiveFilter = activeGroup || area || q || provision;

  // ── Infinite scroll: load the next page when the sentinel enters the viewport ──
  const sentinelRef = useRef(null);
  const loadNext = useCallback(() => {
    if (isFetching || !hasMore) return;
    setSkip((prev) => prev + PAGE_SIZE);
  }, [isFetching, hasMore]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore) return;
    const io = new IntersectionObserver(
      (entries) => { if (entries[0]?.isIntersecting) loadNext(); },
      { rootMargin: "600px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [loadNext, hasMore, allItems.length]);

  return (
    <div className="grad-hero min-h-[70vh]">
      <Seo
        {...PAGE_SEO.updates}
        jsonLd={[
          breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Updates", path: "/updates" },
          ]),
          ...(allItems.length ? [itemListJsonLd(allItems)] : []),
        ]}
      />
      <div className="mx-auto w-full max-w-6xl px-4 py-12 sm:px-6">
        <h1 className="font-serif text-4xl font-bold tracking-tight" data-testid="updates-page-title">{t("latest_updates")}</h1>
        <p className="mt-2 max-w-2xl text-muted-foreground">{t("latest_updates_sub")}</p>

        {/* Ask a legal question — live autocomplete across questions, sections & case titles */}
        <div className="mt-6 flex flex-col gap-3 rounded-2xl border bg-card p-4 shadow-sm sm:flex-row sm:items-center">
          <LegalQuestionSearch
            value={q}
            onChange={handleSearch}
            autoNavigate={false}
            placeholder={lang === "hi" ? t("search_placeholder") : "Search your legal question, section, or case name..."}
            className="flex-1"
            testId="updates-search-input"
          />
          {hasActiveFilter && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-1.5 shrink-0" data-testid="updates-clear-filter">
              <X className="h-4 w-4" /> Clear filters
            </Button>
          )}
        </div>

        {/* TIER 1: Umbrella group pills */}
        <div className="mt-5 flex flex-wrap gap-2" data-testid="updates-group-filter">
          <button
            onClick={() => setActiveGroup("")}
            className={cn(
              "inline-flex items-center rounded-full border px-3.5 py-1.5 text-sm font-medium transition-all duration-150",
              !activeGroup
                ? "bg-primary text-primary-foreground border-primary shadow-sm"
                : "bg-card border-border text-muted-foreground hover:text-foreground hover:border-foreground/30"
            )}
            data-testid="group-pill-all"
          >
            All Categories
          </button>
          {UMBRELLA_GROUPS.map((g) => {
            const Icon = g.icon;
            const isActive = activeGroup === g.name;
            return (
              <button
                key={g.name}
                onClick={() => setActiveGroup(isActive ? "" : g.name)}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-medium transition-all duration-150",
                  isActive
                    ? cn("shadow-sm", g.chip, "opacity-100")
                    : "bg-card border-border text-muted-foreground hover:text-foreground hover:border-foreground/30"
                )}
                data-testid={`group-pill-${g.name.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
              >
                <Icon className="h-3.5 w-3.5" />
                {lang === "hi" ? g.nameHi : g.name}
              </button>
            );
          })}
        </div>

        {/* TIER 2: Sub-area filter (only shown when a group is active and has sub-areas) */}
        {activeGroup && subAreas.length > 1 && (
          <div className="mt-3 flex flex-wrap gap-2 pl-1" data-testid="updates-subarea-filter">
            <span className="self-center text-xs text-muted-foreground font-medium">Refine:</span>
            <button
              onClick={() => setArea("")}
              className={cn(
                "inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium transition-all duration-150",
                !area
                  ? "bg-foreground text-background border-foreground"
                  : "bg-card border-border text-muted-foreground hover:text-foreground"
              )}
            >
              All {activeGroupObj ? (lang === "hi" ? activeGroupObj.nameHi : activeGroupObj.name) : ""}
            </button>
            {subAreas.map((a) => (
              <button
                key={a}
                onClick={() => setArea(area === a ? "" : a)}
                className={cn(
                  "inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium transition-all duration-150",
                  area === a
                    ? cn("shadow-sm", activeGroupObj?.chip || "bg-primary text-primary-foreground")
                    : "bg-card border-border text-muted-foreground hover:text-foreground"
                )}
                data-testid={`subarea-pill-${a.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
              >
                {a}
              </button>
            ))}
          </div>
        )}

        {/* TIER 3: Legal-provision filter (Act / Section) — hidden by default, toggle to show */}
        {provisionIndex.length > 0 && (
          <div className="mt-4" data-testid="updates-provision-filter">
            <button
              onClick={() => setShowProvisions((v) => !v)}
              className="inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-medium transition-all duration-150 bg-card border-border text-muted-foreground hover:text-foreground hover:border-foreground/30"
              data-testid="provision-toggle-btn"
            >
              <BookText className="h-3.5 w-3.5 text-primary" />
              {lang === "hi" ? "कानूनी प्रावधान के अनुसार खोजें" : "Browse by legal provision"}
              {showProvisions ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            </button>

            {showProvisions && (
              <div className="mt-2 rounded-2xl border bg-card p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="flex items-center gap-1.5 text-sm font-semibold">
                    <BookText className="h-4 w-4 text-primary" /> {lang === "hi" ? "कानूनी प्रावधान के अनुसार खोजें" : "Filter by Act / Section"}
                  </p>
                  {provision && (
                    <Button variant="ghost" size="sm" onClick={() => setProvision("")} className="h-7 gap-1 text-xs" data-testid="provision-clear">
                      <X className="h-3 w-3" /> {provision}
                    </Button>
                  )}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {provisionIndex.map((pv) => {
                    const label = pv.sections ? `${pv.sections}, ${pv.act_name}` : pv.act_name;
                    const isActive = provision === label;
                    return (
                      <button
                        key={label}
                        onClick={() => setProvision(isActive ? "" : label)}
                        className={cn(
                          "inline-flex items-center gap-1 rounded-full border px-3 py-1 font-mono text-xs transition-colors",
                          isActive ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-muted-foreground hover:border-primary/40 hover:text-foreground"
                        )}
                        title={`${pv.count} judgment${pv.count !== 1 ? "s" : ""}`}
                        data-testid={`provision-pill-${label.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                      >
                        {label} <span className="opacity-60">({pv.count})</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Active filter badge */}
        {(activeGroup || area || provision) && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {activeGroup && (
              <Badge variant="secondary" className="gap-1.5 rounded-full text-xs">
                {activeGroupObj && <activeGroupObj.icon className="h-3 w-3" />}
                {lang === "hi" ? activeGroupObj?.nameHi : activeGroup}
              </Badge>
            )}
            {area && (
              <Badge variant="secondary" className="rounded-full text-xs">
                {area}
              </Badge>
            )}
            {provision && (
              <Badge variant="outline" className="rounded-full font-mono text-xs" data-testid="active-provision-badge">
                {provision}
              </Badge>
            )}
          </div>
        )}

        {/* Results count */}
        {!isLoading && total > 0 && (
          <p className="mt-4 text-sm text-muted-foreground" data-testid="updates-count">
            Showing {allItems.length} of {total} judgment{total !== 1 ? "s" : ""}
          </p>
        )}

        {/* Judgment grid */}
        <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {isLoading && skip === 0 ? (
            Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-80 w-full rounded-2xl" />)
          ) : allItems.length ? (
            allItems.map((j, i) => <JudgmentCard key={j.id} judgment={j} index={i} />)
          ) : (
            <div className="col-span-full flex flex-col items-center py-16 text-center">
              <p className="text-muted-foreground">{t("no_results")}</p>
              {hasActiveFilter && (
                <Button variant="outline" size="sm" className="mt-4" onClick={clearFilters}>Clear filters</Button>
              )}
            </div>
          )}
        </div>

        {/* Infinite scroll sentinel + loading state */}
        {hasMore && (
          <div ref={sentinelRef} className="mt-8 flex flex-col items-center gap-4" data-testid="infinite-scroll-sentinel" aria-live="polite">
            {isFetching && skip > 0 ? (
              <>
                <div className="grid w-full gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {Array.from({ length: 3 }).map((_, i) => <Skeleton key={`sk-${i}`} className="h-80 w-full rounded-2xl" />)}
                </div>
                <p className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading more judgments…</p>
              </>
            ) : (
              <p className="text-xs text-muted-foreground">Scroll to load more</p>
            )}
          </div>
        )}
        {!hasMore && allItems.length > 0 && !isLoading && (
          <p className="mt-10 flex items-center justify-center gap-2 text-sm text-muted-foreground" data-testid="updates-end">
            <CheckCircle2 className="h-4 w-4 text-emerald-600" /> You have reached the end — all {total} judgments loaded.
          </p>
        )}
      </div>
    </div>
  );
}
